import { MPEGDecoder } from "mpg123-decoder";

const max = (a: number, b: number) => (a > b ? a : b);
const min = (a: number, b: number) => (a < b ? a : b);
const floatToInt = (val: number) => (val > 0 ? min(val * 32767, 32767) : max(val * 32767, -32768));

const getInterleaved = (channelData: Float32Array[], samples: number) => {
  const interleaved = new Int16Array(samples * channelData.length);

  for (let offset = 0, interleavedOffset = 0; offset < samples; offset++) {
    interleaved[interleavedOffset++] = floatToInt(channelData[0][offset]);
    interleaved[interleavedOffset++] = floatToInt(channelData[1][offset]);
  }

  return new Uint8Array(interleaved.buffer);
};

const stringToUint8Array = (string: string) => {
  const buf = new Uint8Array(string.length);

  for (let i = 0; i < string.length; i++) buf[i] = string.charCodeAt(i);

  return buf;
};

const generateHeader = ({
  dataLength = 4294967295 - 100,
  channels = 2,
  sampleRate = 44100,
  bitDepth = 16
}) => {
  const RIFF = stringToUint8Array("RIFF");
  const WAVE = stringToUint8Array("WAVE");
  const fmt = stringToUint8Array("fmt ");
  const data = stringToUint8Array("data");

  const format = 1; // raw PCM

  const headerLength = 44;
  const fileSize = headerLength + dataLength;

  const header = new Uint8Array(headerLength);
  const headerView = new DataView(header.buffer);

  let offset = 0;

  header.set(RIFF, offset);
  offset += RIFF.length;

  headerView.setInt32(offset, fileSize - 8, true);
  offset += 4;

  header.set(WAVE, offset);
  offset += WAVE.length;

  header.set(fmt, offset);
  offset += fmt.length;

  // Write the size of the "fmt " chunk.
  // Value of 16 is hard-coded for raw PCM format. other formats have different size.
  headerView.setUint32(offset, 16, true);
  offset += 4;

  headerView.setUint16(offset, format, true);
  offset += 2;

  headerView.setUint16(offset, channels, true);
  offset += 2;

  headerView.setUint32(offset, sampleRate, true);
  offset += 4;

  const byteRate = (sampleRate * channels * bitDepth) / 8;
  headerView.setUint32(offset, byteRate, true);
  offset += 4;

  const blockAlign = (channels * bitDepth) / 8;
  headerView.setUint16(offset, blockAlign, true);
  offset += 2;

  headerView.setUint16(offset, bitDepth, true);
  offset += 2;

  header.set(data, offset);
  offset += data.length;

  headerView.setUint32(offset, dataLength, true);
  offset += 4;

  return header;
};

const createArrayBuffer = (
  channelData: Float32Array[],
  samplesDecoded: number,
  sampleRate: number
): ArrayBuffer => {
  const interleaved = getInterleaved(channelData, samplesDecoded);
  const waveHeader = generateHeader({
    dataLength: interleaved.length * Int16Array.BYTES_PER_ELEMENT,
    channels: channelData.length,
    sampleRate,
    bitDepth: 16
  });

  const decoded = new Uint8Array(waveHeader.length + interleaved.length);
  decoded.set(waveHeader);
  decoded.set(interleaved, waveHeader.length);

  return decoded.buffer;
};

export class AudioDecoderPlayer {
  audioContext?: AudioContext;
  decoder?: MPEGDecoder;
  initializingPlayback = false;
  source?: AudioBufferSourceNode;

  constructor() {
    this.createAudioContext();
    this.createDecoder();
  }

  createAudioContext() {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;

    if (!AudioContext) return;

    this.audioContext = new AudioContext();
  }

  createBufferSource() {
    if (!this.audioContext) return;

    this.source = this.audioContext.createBufferSource();
  }

  createDecoder() {
    this.decoder = new MPEGDecoder();
  }

  decodeAudioData(audioBuffer: ArrayBuffer): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      this.audioContext?.decodeAudioData(
        audioBuffer,
        (decodedAudioData: AudioBuffer) => {
          resolve(decodedAudioData);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  async decodeAudio(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
    if (!this.decoder) throw new Error("Decoder not available");

    await this.decoder.ready;

    const array = new Uint8Array(arrayBuffer);
    const { channelData, samplesDecoded, sampleRate } = this.decoder.decode(array);

    return createArrayBuffer(channelData, samplesDecoded, sampleRate);
  }

  decodedAudioSuccess = async (decodedAudioData: AudioBuffer) => {
    console.log("decoding successful");
    if (!this.audioContext) return;

    this.createBufferSource();

    if (!this.source) return;

    this.source.buffer = decodedAudioData;

    this.source.onended = () => {
      console.log("playback ended");
    };

    this.source.connect(this.audioContext.destination);

    this.source.start(0);
    console.log("playback started");
  };

  async destroy() {
    this.stop();
    await this.decoder?.free();
  }

  async fallbackDecode(arrayBuffer: ArrayBuffer) {
    if (!this.audioContext) return;

    console.log("attempting fallback decode");

    try {
      const decodedArrayBuffer = await this.decodeAudio(arrayBuffer);
      await this.resetDecoder();

      const decodedAudioData = await this.decodeAudioData(decodedArrayBuffer);

      await this.decodedAudioSuccess(decodedAudioData);
    } catch (error) {
      console.log("fallback decode failed");
    }
  }

  async play(audioBuffer: ArrayBuffer) {
    if (this.initializingPlayback || !this.audioContext) return;
    this.initializingPlayback = true;

    await this.wake();

    this.stop();

    try {
      const decodedAudioData = await this.decodeAudioData(audioBuffer);

      await this.decodedAudioSuccess(decodedAudioData);
    } catch (error) {
      console.log("decodeAudioData caught exception", error);
      await this.fallbackDecode(audioBuffer);
    } finally {
      this.initializingPlayback = false;
    }
  }

  async resetDecoder() {
    await this.decoder?.reset();
  }

  stop() {
    if (!this.source || !this.audioContext) return;

    this.source.disconnect();

    console.log("source disconnected");
  }

  async wake() {
    if (!this.audioContext) return;

    // browser spec doesn't have doesn't have "interrupted" state
    // it is only for webkitAudioContext
    if (
      this.audioContext.state === "suspended" ||
      (this.audioContext.state as any) === "interrupted"
    ) {
      await this.audioContext.resume();
      console.log("resumed");
    }
  }
}
